/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Button, ButtonProps} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface SecondaryButtonProps extends ButtonProps {
  children: string;
}

const useStyles = makeStyles(theme => ({
  container : {
    padding   : '5px',
    '&:focus' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    }
  },
  disabledContainer : {
    outline : 'none'
  },
  root : {
    backgroundImage    : `linear-gradient(-75deg, transparent 50%, ${dlsColor('grey1')} 50%)`,
    backgroundPosition : '100%',
    backgroundSize     : '300%',
    transition         : 'background .5s ease-out',
    borderColor        : dlsColor('grey1'),
    color              : dlsColor('grey1'),
    borderWidth        : dlsAlt('sizes', 'btn', 'secondaryBorderWidth'),
    fontSize           : theme.typography.fontSize,
    lineHeight         : '24px',
    '&:hover'          : {
      backgroundPosition : 0,
      boxShadow          : 'none',
      color              : dlsColor('white')
    },
    '&:active' : {
      backgroundImage : `linear-gradient(-75deg, transparent 50%, ${dlsColor('grey1')} 50%)`
    },
    '&:disabled' : {
      background      : dlsAlt('components', 'btn', 'disabledBackground'),
      borderWidth     : 0,
      backgroundImage : 'none'
    }
  },
  rootAlt : {
    backgroundImage    : `linear-gradient(-75deg, transparent 50%, ${dlsColor('grey1')} 50%)`,
    backgroundPosition : '100%',
    backgroundSize     : '300%',
    transition         : 'background .5s ease-out',
    borderColor        : dlsColor('grey1'),
    color              : dlsColor('grey1'),
    borderWidth        : dlsAlt('sizes', 'btn', 'secondaryBorderWidth'),
    fontSize           : theme.typography.fontSize,
    lineHeight         : '24px',
    '&:hover'          : {
      backgroundPosition : 0,
      boxShadow          : 'none',
      color              : dlsColor('white')
    },
    '&:active' : {
      backgroundImage : `linear-gradient(-75deg, transparent 50%, ${dlsColor('grey1')} 50%)`
    },
    '&:disabled' : {
      background      : dlsAlt('components', 'btn', 'disabledBackground'),
      borderWidth     : 0,
      backgroundImage : 'none'
    }
  },
  disabled : {}
}));

/**
 * Secondary button component used for medium-emphasis actions.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import {SecondaryButton} from '@ion/dls/src/components/buttons';
 *
 * const Example = () => (
 *   <>
 *     <SecondaryButton>Default</SecondaryButton>
 *     <SecondaryButton disabled>Disabled</SecondaryButton>
 *     <SecondaryButton fullWidth>Full Width</SecondaryButton>
 *   </>
 * );
 * ```
 */
const SecondaryButton = ({children, ...props}: SecondaryButtonProps) => {
  const classes = useStyles();
  return (
    <div className={props.disabled ? classes.disabledContainer : classes.container}
      tabIndex={props.disabled ? -1 : 0}>
      <Button variant='outlined'
        tabIndex={-1}
        classes={{
          root     : props.fullWidth ? classes.rootAlt : classes.root,
          disabled : classes.disabled
        }}
        disableRipple
        disableFocusRipple
        {...props}>
        {children}
      </Button>
    </div>
  );
};

export {SecondaryButton};
